import React, { useEffect } from 'react';
import { Modal } from '../Modal/Modal';
import { Input } from '../Input/Input';
import { DatePicker } from '../DatePicker/DatePicker';
import { Select } from '../Select/Select';
import { Button } from '../Button/Button';
import { useAddCaseForm, useAgencyOptions, useOfficeOptions } from './hooks';
import './AddCaseForm.scss';

interface AddCaseFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: any) => void;
  eventId: number;
  participantId?: number;
  allowedAgencyNames?: string;
  allowedOfficeNames?: string;
}

export const AddCaseForm: React.FC<AddCaseFormProps> = ({
  isOpen,
  onClose,
  onSubmit,
  eventId,
  participantId,
  allowedAgencyNames,
  allowedOfficeNames,
}) => {
  const { formData, handleChange, handleSelectChange, handleDateChange, handleSave } =
    useAddCaseForm({ eventId, participantId, onSubmit, onClose });

  const { agencyOptions } = useAgencyOptions({ eventId, allowedAgencyNames, isOpen });
  const { officeOptions } = useOfficeOptions({
    eventId,
    participantId,
    allowedOfficeNames,
    isOpen,
  });

  useEffect(() => {
    if (agencyOptions.length === 1) {
      handleSelectChange('agency', agencyOptions[0].value);
    }
  }, [agencyOptions, handleSelectChange]);

  useEffect(() => {
    if (officeOptions.length === 1) {
      handleSelectChange('office', officeOptions[0].value);
    } else if (formData.office && !officeOptions.some((o) => o.value === formData.office)) {
      handleSelectChange('office', '');
    }
  }, [officeOptions, formData.office, handleSelectChange]);

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className="add-case-form">
        <h2 className="form-title">Add Case</h2>
        <div className="form-content">
          <Input
            label="Case Hours"
            name="caseHours"
            placeholder="Enter the quantity"
            value={formData.caseHours}
            onChange={handleChange}
          />
          <Input
            label="Client's ID"
            name="clientId"
            placeholder="Enter the number here"
            value={formData.clientId}
            onChange={handleChange}
          />
          <Input
            label="Client's First Name"
            name="clientFirstName"
            placeholder="Enter the first name"
            value={formData.clientFirstName}
            onChange={handleChange}
          />
          <Input
            label="Client's Last Name"
            name="clientLastName"
            placeholder="Enter the last name"
            value={formData.clientLastName}
            onChange={handleChange}
          />
          <Input
            label="Caregiver's Name"
            name="caregiverName"
            placeholder="Enter the Caregiver's Name"
            value={formData.caregiverName}
            onChange={handleChange}
          />
          <DatePicker
            id="socDate"
            label="SOC (Start of Care) Date"
            name="socDate"
            value={formData.socDate}
            onChange={(date) => handleDateChange('socDate', date)}
          />
          <DatePicker
            id="referralDate"
            label="Referral Date"
            name="referralDate"
            value={formData.referralDate}
            onChange={(date) => handleDateChange('referralDate', date)}
          />
          <Select
            label="Agency"
            name="agency"
            options={agencyOptions}
            value={formData.agency}
            onChange={(value) => handleSelectChange('agency', value)}
            placeholder="Choose Agency"
          />
          <Select
            label="Office"
            name="office"
            options={officeOptions}
            value={formData.office}
            onChange={(value) => handleSelectChange('office', value)}
            placeholder="Choose Office"
          />
          <div className="form-actions">
            <Button variant="button" className="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button variant="button" onClick={handleSave}>
              Add Case
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};
